import React, { useEffect, useRef, useState } from 'react';
import MatrixRain from './MatrixRain';
import HandcuffLock from './HandcuffLock';

const PASSWORD = 'ANL@SURI';
const TITLE_SRC = 'https://customer-assets.emergentagent.com/job_soori-central/artifacts/3pwucspq_BLACK_AXE_title_transparent.png';

export default function Gate({ onUnlock }) {
  const [value, setValue] = useState('');
  const [shake, setShake] = useState(0);
  const [unlocked, setUnlocked] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [show, setShow] = useState(true);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    // Focus the input shortly after mount so the user can type immediately.
    const t = setTimeout(() => inputRef.current?.focus(), 600);
    return () => clearTimeout(t);
  }, []);

  const tryUnlock = () => {
    if (unlocked) return;
    if (value === PASSWORD) {
      setUnlocked(true);
      setError('');
      // Wait for cuff-open + fade, then notify parent
      setTimeout(() => setExiting(true), 900);
      setTimeout(() => {
        setShow(false);
        onUnlock?.();
      }, 1800);
    } else {
      setShake((s) => s + 1);
      setError('ACCESS DENIED');
      setTimeout(() => setError(''), 1400);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter') tryUnlock();
  };

  if (!show) return null;

  return (
    <div
      data-testid="gate-overlay"
      className="fixed inset-0 z-[9999]"
      style={{
        opacity: exiting ? 0 : 1,
        transition: 'opacity 900ms ease',
        background:
          'radial-gradient(ellipse at center, #1a0d05 0%, #0a0603 55%, #000 100%)',
      }}
    >
      {/* Hero silhouette image (very dim, painterly) */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            'url("https://customer-assets.emergentagent.com/job_soori-central/artifacts/f9uyuhjt_hf_20260628_182854_5ac7656d-84e8-40a1-a9df-305632369c7c%20%281%29.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'brightness(0.32) contrast(1.05) saturate(0.7)',
          opacity: 0.65,
        }}
      />

      {/* Warm vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(0,0,0,0) 35%, rgba(0,0,0,0.85) 100%)',
        }}
      />

      {/* Golden matrix rain — top layer */}
      <MatrixRain opacity={0.5} fontSize={16} density={1.05} />

      {/* Content */}
      <div className="relative h-full w-full flex flex-col items-center justify-center px-6" style={{ zIndex: 5 }}>
        {/* BLACK AXE golden title */}
        <img
          src={TITLE_SRC}
          alt="BLACK AXE"
          data-testid="gate-title"
          className="select-none"
          draggable={false}
          style={{
            width: 'min(820px, 88vw)',
            height: 'auto',
            filter: 'drop-shadow(0 0 24px rgba(255,180,80,0.45))',
            animation: 'gateTitleIn 1.4s cubic-bezier(.2,.7,.2,1) both',
          }}
        />

        {/* Subtitle */}
        <div
          className="mt-2 mb-8 text-center"
          style={{
            color: '#d9b46a',
            letterSpacing: '0.42em',
            fontSize: 12,
            fontFamily: '"Share Tech Mono", monospace',
            animation: 'gateFade 1.6s 0.4s ease both',
          }}
        >
          ENCRYPTED · CLASSIFIED · UNAUTHORIZED ACCESS WILL BE LOGGED
        </div>

        {/* Handcuff lock */}
        <div
          className={shake ? 'gate-shake' : ''}
          key={shake}
          style={{ animation: 'gateFade 1.4s 0.6s ease both' }}
        >
          <HandcuffLock unlocked={unlocked} attempt={shake} />
        </div>

        {/* Password field */}
        <div
          className="mt-10 w-full max-w-md"
          style={{ animation: 'gateFade 1.4s 0.9s ease both' }}
        >
          <label
            htmlFor="gate-pwd"
            className="block text-center mb-3"
            style={{
              fontFamily: '"Share Tech Mono", monospace',
              fontSize: 11,
              letterSpacing: '0.5em',
              color: '#b58a3c',
            }}
          >
            ENTER CIPHER
          </label>

          <div className="relative">
            <input
              ref={inputRef}
              id="gate-pwd"
              data-testid="gate-password-input"
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={value}
              disabled={unlocked}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={onKey}
              placeholder="• • • • • • • •"
              className="w-full text-center"
              style={{
                background: 'rgba(10, 6, 2, 0.7)',
                border: '1px solid rgba(214,164,72,0.35)',
                color: '#ffe6a4',
                padding: '14px 18px',
                letterSpacing: '0.6em',
                fontFamily: '"Share Tech Mono", monospace',
                fontSize: 18,
                outline: 'none',
                borderRadius: 2,
                boxShadow:
                  'inset 0 0 24px rgba(255,180,80,0.08), 0 0 14px rgba(255,180,80,0.18)',
              }}
            />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                border: '1px solid rgba(255,210,120,0.15)',
                borderRadius: 2,
                animation: 'gatePulse 2.6s ease-in-out infinite',
              }}
            />
          </div>

          <button
            data-testid="gate-unlock-btn"
            onClick={tryUnlock}
            disabled={unlocked}
            className="mt-5 w-full group relative overflow-hidden"
            style={{
              padding: '12px 18px',
              background:
                'linear-gradient(180deg, rgba(214,164,72,0.18), rgba(40,24,8,0.6))',
              border: '1px solid rgba(214,164,72,0.55)',
              color: '#ffe6a4',
              letterSpacing: '0.45em',
              fontFamily: '"Share Tech Mono", monospace',
              fontSize: 12,
              borderRadius: 2,
              cursor: unlocked ? 'default' : 'pointer',
              transition: 'all 220ms ease',
            }}
          >
            {unlocked ? 'ACCESS GRANTED · ENTERING' : 'UNLOCK · DECRYPT'}
          </button>

          <div
            data-testid="gate-error"
            className="text-center mt-4"
            style={{
              minHeight: 18,
              color: '#ff5a3a',
              letterSpacing: '0.4em',
              fontFamily: '"Share Tech Mono", monospace',
              fontSize: 11,
              opacity: error ? 1 : 0,
              transition: 'opacity 200ms ease',
            }}
          >
            {error || ' '}
          </div>
        </div>

        {/* Footer hint */}
        <div
          className="absolute bottom-6 left-0 right-0 text-center"
          style={{
            color: 'rgba(214,164,72,0.45)',
            letterSpacing: '0.5em',
            fontSize: 10,
            fontFamily: '"Share Tech Mono", monospace',
          }}
        >
          A FILM BY ANL ARASU · SOORI IN AND AS BLACK AXE
        </div>
      </div>

      <style>{`
        @keyframes gateTitleIn {
          0%   { opacity: 0; transform: translateY(-18px) scale(0.96); filter: blur(8px) drop-shadow(0 0 0 rgba(255,180,80,0)); }
          60%  { opacity: 1; filter: blur(0) drop-shadow(0 0 24px rgba(255,180,80,0.45)); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes gateFade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes gatePulse { 0%,100% { box-shadow: 0 0 0 rgba(255,210,120,0); } 50% { box-shadow: 0 0 18px rgba(255,210,120,0.18); } }
        @keyframes gateShake {
          0%, 100% { transform: translateX(0); }
          15% { transform: translateX(-10px); }
          30% { transform: translateX(10px); }
          45% { transform: translateX(-7px); }
          60% { transform: translateX(7px); }
          80% { transform: translateX(-3px); }
        }
        .gate-shake { animation: gateShake 480ms ease-out; }
      `}</style>
    </div>
  );
}
